// FilterPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.css";
import { listUserTags, listSystemTags, listMakes } from "./data";
import logos from "./Assets/logos.json";

export const DEFAULT_FILTERS = {
    severity: "any",
    dayNight: "any",
    tagKeys: [],
    makes: [],
    alertOnly: false,

};

function useOutsideClick(ref, onOutside) {
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onOutside?.();
        };
        document.addEventListener("mousedown", handler, true);
        return () => document.removeEventListener("mousedown", handler, true);
    }, [ref, onOutside]);
}

const sevClass = (s) => (s === "severe" ? "bad" : s === "warning" ? "unk" : "ok");
const sevLabel = (s) => (s === "severe" ? "Severe" : s === "warning" ? "Warning" : "Normal");

export default function FilterPanel({
    anchorRef,
    initialVRM = "",
    initialFilters = DEFAULT_FILTERS,
    onApply,
    onClose,
}) {
    const panelRef = useRef(null);
    useOutsideClick(panelRef, onClose);

    const [vrm, setVrm] = useState(initialVRM);
    const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...initialFilters }));
    const vrmInputRef = useRef(null);


    // logos map
    const logoMap = useMemo(() => {
        const m = new Map();
        try {
            (Array.isArray(logos) ? logos : []).forEach((it) => {
                const name = String(it?.name || "").toUpperCase();
                const url = it?.image?.optimized || it?.image?.url || it?.url;
                if (name && url && !m.has(name)) m.set(name, url);
            });
        } catch { }
        return m;
    }, []);
    const getLogoUrl = (mk) => logoMap.get(String(mk || "").toUpperCase()) || null;

    // options
    const [userTags, setUserTags] = useState([]);
    const [systemTags, setSystemTags] = useState([]);
    const [makeOptions, setMakeOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ok = true;
        (async () => {
            try {
                const [u, s, m] = await Promise.all([
                    listUserTags().catch(() => []),
                    listSystemTags().catch(() => []),
                    listMakes().catch(() => []),
                ]);
                if (!ok) return;
                setUserTags((u || []).map(t => ({ ...t, source: "user" })));
                setSystemTags((s || []).map(t => ({ ...t, source: "system" })));
                setMakeOptions(m || []);
            } finally {
                if (ok) setLoading(false);
            }
        })();
        return () => { ok = false; };
    }, []);

    // position
    const position = useMemo(() => {
        const btn = anchorRef?.current;
        const rect = btn ? btn.getBoundingClientRect() : { right: 0, bottom: 0, width: 0 };
        const margin = 12;
        const vw = window.innerWidth;
        const desiredW = 520;
        const w = Math.min(Math.max(desiredW, rect.width), Math.max(320, vw - margin * 2));
        const left = Math.min(vw - margin - w, Math.max(margin, rect.right - w));
        const top = rect.bottom + 8;
        return { top, left, width: w };
    }, [anchorRef]);

    // ===== TAGS =====
    const [q, setQ] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const tagInputRef = useRef(null);

    const selectedTagSet = useMemo(() => new Set(filters.tagKeys || []), [filters.tagKeys]);

    const allTags = useMemo(() => {
        const map = new Map();
        [...systemTags, ...userTags].forEach(t => { if (t?.key && !map.has(t.key)) map.set(t.key, t); });
        const arr = Array.from(map.values());
        arr.sort((a, b) => String(a.label || a.key).localeCompare(String(b.label || b.key), "en", { sensitivity: "base" }));
        return arr;
    }, [systemTags, userTags]);

    const availableTags = useMemo(() => {
        const norm = (s) => String(s || "").toLowerCase();
        const query = norm(q);
        return allTags
            .filter(t => !selectedTagSet.has(t.key))
            .filter(t => !query || norm(t.key).includes(query) || norm(t.label).includes(query))
            .slice(0, 50);
    }, [allTags, selectedTagSet, q]);

    const addTagAndClose = (key) => {
        if (!key || selectedTagSet.has(key)) return;
        setFilters(f => ({ ...f, severity: "any", tagKeys: [...(f.tagKeys || []), key] }));
        setQ(""); setActiveIdx(0); setSuggestOpen(false); tagInputRef.current?.blur();
    };
    const removeTagKey = (key) => setFilters(f => ({ ...f, tagKeys: (f.tagKeys || []).filter(k => k !== key) }));

    const onTagKeyDown = (e) => {
        if (e.key === "Escape") { setSuggestOpen(false); tagInputRef.current?.blur(); return; }
        if (!availableTags.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, availableTags.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); addTagAndClose(availableTags[Math.max(0, Math.min(activeIdx, availableTags.length - 1))].key); }
    };

    const tagsByKey = useMemo(() => {
        const m = new Map(); allTags.forEach(t => m.set(t.key, t)); return m;
    }, [allTags]);

    // ===== MAKES =====
    const [mq, setMq] = useState("");
    const [mActiveIdx, setMActiveIdx] = useState(0);
    const [mSuggestOpen, setMSuggestOpen] = useState(false);
    const makeInputRef = useRef(null);

    const selectedMakeSet = useMemo(() => new Set(filters.makes || []), [filters.makes]);

    // REMOVE the 50-item cap so you can scroll through all makes
    const availableMakes = useMemo(() => {
        const norm = (s) => String(s || "").toLowerCase();
        const query = norm(mq);
        return makeOptions
            .filter(mk => !selectedMakeSet.has(mk))
            .filter(mk => !query || norm(mk).includes(query));
    }, [makeOptions, selectedMakeSet, mq]);

    const addMakeAndClose = (mk) => {
        if (!mk || selectedMakeSet.has(mk)) return;
        setFilters(f => ({ ...f, makes: [...(f.makes || []), mk] }));
        setMq(""); setMActiveIdx(0); setMSuggestOpen(false); makeInputRef.current?.blur();
    };
    const removeMake = (mk) => setFilters(f => ({ ...f, makes: (f.makes || []).filter(x => x !== mk) }));

    const onMakeKeyDown = (e) => {
        if (e.key === "Escape") { setMSuggestOpen(false); makeInputRef.current?.blur(); return; }
        if (!availableMakes.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setMActiveIdx(i => Math.min(i + 1, availableMakes.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setMActiveIdx(i => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); addMakeAndClose(availableMakes[Math.max(0, Math.min(mActiveIdx, availableMakes.length - 1))]); }
    };

    // mutual de-emphasis
    const hasTags = (filters.tagKeys?.length || 0) > 0;
    const severityActive = filters.severity !== "any";
    const deemphSeverity = hasTags;
    const deemphTags = severityActive;
    const deemphStyle = { opacity: 0.88 };

    const setSeverity = (level) => {
        setFilters(f => (level === "any" ? { ...f, severity: "any" } : { ...f, severity: level, tagKeys: [] }));
    };

    const tagStackStyle = { position: "relative", zIndex: 3, ...(deemphTags ? deemphStyle : {}) };
    const makeStackStyle = { position: "relative", zIndex: 1 };

    // apply/reset
    const handleApply = () => {
        const cleanVRM = (vrm || "").trim().toUpperCase();
        onApply?.(cleanVRM, filters);
        onClose?.();
    };
    const handleReset = () => {
        setVrm(""); setFilters(DEFAULT_FILTERS);
        setQ(""); setSuggestOpen(false); setMq(""); setMSuggestOpen(false);
    };

    // visuals
    const miniPill = { padding: "3px 8px", fontSize: ".75rem", lineHeight: 1.05 };
    const LOGO_SIZE = 44;
    const logoTile = {
        width: LOGO_SIZE, minWidth: LOGO_SIZE, height: LOGO_SIZE,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: "#fff", border: "1px solid #ececec", borderRadius: 8,
        boxShadow: "0 1px 1px rgba(0,0,0,.04)", overflow: "hidden",
    };
    const logoImg = { maxWidth: "90%", maxHeight: "90%", objectFit: "contain", filter: "saturate(.95) contrast(1.05)", mixBlendMode: "multiply" };

    // reusable scroll style for dropdowns
    const scrollListStyle = { maxHeight: "46vh", overflowY: "auto", overscrollBehavior: "contain" };

    return (
        <div
            ref={panelRef}
            className={styles.filterPanel}
            style={{ position: "fixed", top: position.top, left: position.left, width: position.width, maxWidth: "92vw" }}
            role="dialog"
            aria-label="Filters"
        >
            <div className={styles.filterPanelHeader}>
                <div className={styles.filterTitle}>Filters</div>
                <button type="button" className={styles.xBtn} onClick={onClose} aria-label="Close filters">×</button>
            </div>

            {/* VRM */}
            <div className={styles.filterSection}>
                <label className={styles.fieldLabel} htmlFor="vrm-input">VRM (full or partial)</label>
                <div className={styles.inputWrap}>
                    <input
                        id="vrm-input"
                        ref={vrmInputRef}
                        className={`${styles.textInput} ${vrm ? styles.withClear : ""}`}
                        placeholder="e.g. AB12, DY70FYJ"
                        value={vrm}
                        onChange={(e) => setVrm(e.target.value)}
                    />
                    {vrm && (
                        <button
                            type="button"
                            className={styles.inputClearBtn}
                            aria-label="Clear VRM"
                            onClick={() => { setVrm(""); vrmInputRef.current?.focus(); }}
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.filterSection}>
                <label className={styles.fieldLabel} htmlFor="alert-only-toggle">
                    Alert list
                </label>
                <div className={styles.checkboxRow}>
                    <label className={styles.checkLine}>
                        <input
                            id="alert-only-toggle"
                            type="checkbox"
                            checked={!!filters.alertOnly}
                            onChange={(e) =>
                                setFilters((f) => ({ ...f, alertOnly: e.target.checked }))
                            }
                        />
                        Only show vehicles on the alert list
                    </label>
                </div>
            </div>

            {/* Time of day */}
            <div className={styles.filterSection}>
                <div className={styles.fieldLabel}>Time of day</div>
                <div className={styles.segmented}>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-daynight" checked={filters.dayNight === "any"} onChange={() => setFilters(f => ({ ...f, dayNight: "any" }))} />
                        Any
                    </label>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-daynight" checked={filters.dayNight === "day"} onChange={() => setFilters(f => ({ ...f, dayNight: "day" }))} />
                        Day only
                    </label>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-daynight" checked={filters.dayNight === "night"} onChange={() => setFilters(f => ({ ...f, dayNight: "night" }))} />
                        Night only
                    </label>
                </div>
            </div>

            {/* Severity */}
            <div className={styles.filterSection} style={deemphSeverity ? deemphStyle : undefined}>
                <div className={styles.fieldLabel}>Alert severity</div>
                <div className={styles.segmented}>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-sev" checked={filters.severity === "any"} onChange={() => setSeverity("any")} />
                        Off
                    </label>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-sev" checked={filters.severity === "warnOrSevere"} onChange={() => setSeverity("warnOrSevere")} />
                        Warnings + Severe
                    </label>
                    <label className={styles.segment}>
                        <input type="radio" name="fp-sev" checked={filters.severity === "severe"} onChange={() => setSeverity("severe")} />
                        Severe only
                    </label>
                </div>
            </div>

            {/* Selected tags */}
            {(filters.tagKeys?.length || 0) > 0 && (
                <div className={styles.filterSection} style={tagStackStyle}>
                    <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Filter by tags</div>
                    <div className="tags-wrap">
                        {filters.tagKeys.map((k) => {
                            const t = tagsByKey.get(k) || { key: k, label: k, default_status: "normal" };
                            return (
                                <span key={k} className={`status-pill status-pill--tag ${sevClass(t.default_status)}`}>
                                    {t.label || t.key}
                                    <button className="pill-x" type="button" onClick={() => removeTagKey(k)} aria-label={`Remove ${t.label || t.key}`}>×</button>
                                </span>
                            );
                        })}
                        <button type="button" className={styles.clearChips} onClick={() => setFilters(f => ({ ...f, tagKeys: [] }))}>
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {/* Tag search/add */}
            <div className={styles.filterSection} style={tagStackStyle}>
                <label className={styles.fieldLabel} htmlFor="tag-add-input" style={{ marginBottom: 6 }}>Filter by tag</label>
                <div className={styles.tagAddWrap}>
                    <input
                        id="tag-add-input"
                        ref={tagInputRef}
                        className={styles.textInput}
                        placeholder={loading ? "Loading tags…" : "Type to search…"}
                        disabled={loading}
                        value={q}
                        onFocus={() => setSuggestOpen(true)}
                        onBlur={() => setTimeout(() => setSuggestOpen(false), 80)}
                        onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
                        onKeyDown={onTagKeyDown}
                        autoComplete="off"
                        aria-expanded={suggestOpen}
                        aria-autocomplete="list"
                    />
                    {suggestOpen && availableTags.length > 0 && (
                        <ul
                            className={styles.suggestList}
                            role="listbox"
                            aria-label="Available tags"
                            style={scrollListStyle}
                        >
                            {availableTags.map((t, i) => (
                                <li
                                    key={t.key}
                                    role="option"
                                    aria-selected={i === activeIdx}
                                    className={`${styles.suggestItem} ${i === activeIdx ? styles.suggestActive : ""}`}
                                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onMouseDown={(e) => { e.preventDefault(); addTagAndClose(t.key); }}
                                    title={t.label}
                                >
                                    <span className={styles.suggestLabel} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
                                        {t.label}
                                    </span>
                                    <span className={`status-pill status-pill--tag ${sevClass(t.default_status)}`} style={miniPill}>
                                        {sevLabel(t.default_status)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {suggestOpen && q !== "" && availableTags.length === 0 && (
                        <div className={styles.muted} style={{ marginTop: 6 }}>No matching tags</div>
                    )}
                </div>
            </div>

            {/* Selected manufacturers */}
            {(filters.makes?.length || 0) > 0 && (
                <div className={styles.filterSection} style={makeStackStyle}>
                    <div className={styles.fieldLabel} style={{ marginBottom: 6 }}>Selected manufacturers</div>
                    <div className="tags-wrap">
                        {filters.makes.map((mk) => (
                            <span key={mk} className={`status-pill status-pill--tag`} title={mk}>
                                {mk}
                                <button className="pill-x" type="button" onClick={() => removeMake(mk)} aria-label={`Remove ${mk}`}>×</button>
                            </span>
                        ))}
                        <button type="button" className={styles.clearChips} onClick={() => setFilters(f => ({ ...f, makes: [] }))}>
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {/* Manufacturer search/add */}
            <div className={styles.filterSection} style={makeStackStyle}>
                <label className={styles.fieldLabel} htmlFor="make-add-input" style={{ marginBottom: 6 }}>Filter by manufacturer</label>
                <div className={styles.tagAddWrap}>
                    <input
                        id="make-add-input"
                        ref={makeInputRef}
                        className={styles.textInput}
                        placeholder={loading ? "Loading manufacturers…" : "Type to search…"}
                        disabled={loading}
                        value={mq}
                        onFocus={() => setMSuggestOpen(true)}
                        onBlur={() => setTimeout(() => setMSuggestOpen(false), 80)}
                        onChange={(e) => { setMq(e.target.value); setMActiveIdx(0); }}
                        onKeyDown={onMakeKeyDown}
                        autoComplete="off"
                        aria-expanded={mSuggestOpen}
                        aria-autocomplete="list"
                    />
                    {mSuggestOpen && availableMakes.length > 0 && (
                        <ul
                            className={styles.suggestList}
                            role="listbox"
                            aria-label="Available manufacturers"
                            style={scrollListStyle}
                        >
                            {availableMakes.map((mk, i) => {
                                const logoUrl = getLogoUrl(mk);
                                return (
                                    <li
                                        key={mk}
                                        role="option"
                                        aria-selected={i === mActiveIdx}
                                        className={`${styles.suggestItem} ${i === mActiveIdx ? styles.suggestActive : ""}`}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: `${LOGO_SIZE}px 1fr`,
                                            alignItems: "center",
                                            gap: 10,
                                            textAlign: "left",
                                        }}
                                        onMouseEnter={() => setMActiveIdx(i)}
                                        onMouseDown={(e) => { e.preventDefault(); addMakeAndClose(mk); }}
                                        title={mk}
                                    >
                                        <span style={logoTile}>
                                            {logoUrl ? <img src={logoUrl} alt="" aria-hidden="true" style={logoImg} /> : null}
                                        </span>
                                        <span className={styles.suggestLabel} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {mk}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {mSuggestOpen && mq !== "" && availableMakes.length === 0 && (
                        <div className={styles.muted} style={{ marginTop: 6 }}>No matching manufacturers</div>
                    )}
                </div>
            </div>

            <div className={styles.filterActions}>
                <button type="button" className={styles.resetBtn} onClick={handleReset}>Reset</button>
                <div className={styles.flexSpacer} />
                <button type="button" className={styles.secondaryBtn} onClick={onClose}>Cancel</button>
                <button type="button" className={styles.applyBtn} onClick={handleApply}>Apply</button>
            </div>
        </div>
    );
}
